import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Wifi, WifiOff, RefreshCw, Search, Check, X, AlertTriangle, 
  Monitor, Cpu, Car, Activity, Users, Shield, Download, 
  Clock, MoreVertical, ChevronDown, Eye, Ban, Trash2, Plus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge Component
const StatusBadge = ({ status, type = 'default' }) => {
  const getStatusStyle = () => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'connected':
      case 'approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'offline':
      case 'disconnected':
      case 'blocked':
      case 'revoked':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending':
      case 'connecting':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'busy':
      case 'error':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <Badge className={`${getStatusStyle()} border text-xs font-medium`}>
      {status || 'Unknown'}
    </Badge>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400',
    purple: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <Card className="bg-[#1a2332] border-gray-800">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">{label}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subValue && <p className="text-xs text-gray-500">{subValue}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminLiveOBD = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [dongles, setDongles] = useState([]);
  const [releases, setReleases] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Fetch data
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/live-obd/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchSessions = useCallback(async () => {
    try {
      const url = selectedFilter === 'all' 
        ? `${BACKEND_URL}/api/admin/live-obd/sessions`
        : `${BACKEND_URL}/api/admin/live-obd/sessions?state=${selectedFilter}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, [token, selectedFilter]);

  const fetchDongles = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/live-obd/dongles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDongles(data.dongles || []);
      }
    } catch (error) {
      console.error('Error fetching dongles:', error);
    }
  }, [token]);

  const fetchReleases = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/live-obd/releases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReleases(data || []);
      }
    } catch (error) {
      console.error('Error fetching releases:', error);
    }
  }, [token]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchSessions(),
      fetchDongles(),
      fetchReleases()
    ]);
    setLoading(false);
  }, [fetchStats, fetchSessions, fetchDongles, fetchReleases]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchSessions();
    }
  }, [activeTab, selectedFilter, fetchSessions]);

  // Auto-refresh for active sessions
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchStats();
      if (activeTab === 'sessions') {
        fetchSessions();
      }
    }, 10000); // Every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, fetchStats, fetchSessions]);

  // Dongle actions
  const approveDongle = async (dongleId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/live-obd/dongles/${dongleId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'approved' })
      });
      
      if (response.ok) {
        toast.success('Dongle genehmigt');
        fetchDongles();
        fetchStats();
      } else {
        toast.error('Fehler beim Genehmigen');
      }
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    }
  };

  const blockDongle = async (dongleId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/live-obd/dongles/${dongleId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'blocked' })
      });
      
      if (response.ok) {
        toast.success('Dongle blockiert');
        fetchDongles();
        fetchStats();
      } else {
        toast.error('Fehler beim Blockieren');
      }
    } catch (error) {
      toast.error('Fehler beim Blockieren');
    }
  };

  const terminateSession = async (sessionId) => {
    if (!window.confirm('Sitzung wirklich beenden?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/live-obd/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Sitzung beendet');
        fetchSessions();
        fetchStats();
      } else {
        toast.error('Fehler beim Beenden der Sitzung');
      }
    } catch (error) {
      toast.error('Fehler beim Beenden der Sitzung');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return `vor ${diff} Sek.`;
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
    return `vor ${Math.floor(diff / 86400)} Tagen`;
  };

  // Filter sessions by search
  const filteredSessions = sessions.filter(session => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      session.user_email?.toLowerCase().includes(term) ||
      session.agent_id?.toLowerCase().includes(term) ||
      session.connected_vehicle_vin?.toLowerCase().includes(term) ||
      session.os_info?.toLowerCase().includes(term)
    );
  });

  // Filter dongles by search
  const filteredDongles = dongles.filter(dongle => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      dongle.user_email?.toLowerCase().includes(term) ||
      dongle.fingerprint_hash?.toLowerCase().includes(term) ||
      dongle.friendly_name?.toLowerCase().includes(term) ||
      dongle.device_type?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-400" />
            Live OBD Management
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Verwalten Sie Agent-Sitzungen, Dongles und Software-Releases
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh An' : 'Auto-Refresh'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            disabled={loading}
            className="text-white border-gray-600 hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            icon={Users}
            label="Aktive Sitzungen"
            value={stats.sessions?.active || 0}
            subValue={`von ${stats.sessions?.total || 0} gesamt`}
            color="green"
          />
          <StatsCard
            icon={Cpu}
            label="Genehmigte Dongles"
            value={stats.dongles?.approved || 0}
            subValue={stats.dongles?.pending > 0 ? `${stats.dongles.pending} ausstehend` : 'Keine ausstehenden'}
            color={stats.dongles?.pending > 0 ? 'yellow' : 'blue'}
          />
          <StatsCard
            icon={Download}
            label="Aktuelle Version"
            value={stats.releases?.latest_version || '1.0.0'}
            subValue={`${stats.releases?.total || 0} Releases`}
            color="purple"
          />
          <StatsCard
            icon={Shield}
            label="Ausstehende Genehmigungen"
            value={stats.dongles?.pending || 0}
            subValue="Dongles warten"
            color={stats.dongles?.pending > 0 ? 'red' : 'green'}
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#1a2332] border border-gray-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">
            <Activity className="w-4 h-4 mr-2" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="sessions" className="data-[state=active]:bg-blue-600">
            <Monitor className="w-4 h-4 mr-2" />
            Sitzungen ({sessions.length})
          </TabsTrigger>
          <TabsTrigger value="dongles" className="data-[state=active]:bg-blue-600">
            <Cpu className="w-4 h-4 mr-2" />
            Dongles ({dongles.length})
            {stats?.dongles?.pending > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-black text-xs">{stats.dongles.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="releases" className="data-[state=active]:bg-blue-600">
            <Download className="w-4 h-4 mr-2" />
            Releases ({releases.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Active Sessions */}
            <Card className="bg-[#1a2332] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-green-400" />
                  Aktive Verbindungen
                </CardTitle>
                <CardDescription>Derzeit verbundene Agents</CardDescription>
              </CardHeader>
              <CardContent>
                {sessions.filter(s => s.state === 'online').length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <WifiOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Keine aktiven Verbindungen</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sessions.filter(s => s.state === 'online').slice(0, 5).map(session => (
                      <div key={session.id} className="flex items-center justify-between p-3 bg-[#151c28] rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          <div>
                            <p className="text-white text-sm">{session.user_email}</p>
                            <p className="text-gray-500 text-xs">{session.os_info || 'Unknown OS'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <StatusBadge status={session.adapter_state} />
                          <p className="text-gray-500 text-xs mt-1">
                            {formatTimeAgo(session.last_heartbeat)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Dongles */}
            <Card className="bg-[#1a2332] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Ausstehende Dongle-Genehmigungen
                </CardTitle>
                <CardDescription>Neue Dongles warten auf Freigabe</CardDescription>
              </CardHeader>
              <CardContent>
                {dongles.filter(d => d.status === 'pending').length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Check className="w-12 h-12 mx-auto mb-2 opacity-50 text-green-500" />
                    <p>Keine ausstehenden Genehmigungen</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dongles.filter(d => d.status === 'pending').slice(0, 5).map(dongle => (
                      <div key={dongle.id} className="flex items-center justify-between p-3 bg-[#151c28] rounded-lg">
                        <div>
                          <p className="text-white text-sm">{dongle.friendly_name || dongle.device_type || 'Unbekannter Dongle'}</p>
                          <p className="text-gray-500 text-xs">{dongle.user_email}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            onClick={() => approveDongle(dongle.id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => blockDongle(dongle.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Agent Sitzungen</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-[#151c28] border-gray-700 w-64"
                    />
                  </div>
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="bg-[#151c28] border border-gray-700 rounded-md px-3 py-2 text-white text-sm"
                  >
                    <option value="all">Alle Status</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                    <option value="busy">Beschäftigt</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Benutzer</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Adapter</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Fahrzeug</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Version</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Letzter Heartbeat</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map(session => (
                      <tr key={session.id} className="border-b border-gray-800/50 hover:bg-[#151c28]/50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white text-sm">{session.user_email || '-'}</p>
                            <p className="text-gray-500 text-xs">{session.os_info || 'Unknown'}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={session.state} />
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={session.adapter_state} />
                        </td>
                        <td className="py-3 px-4">
                          {session.connected_vehicle_vin ? (
                            <div>
                              <p className="text-white text-sm">{session.connected_vehicle_vin}</p>
                              <p className="text-gray-500 text-xs">
                                {session.connected_vehicle_brand} {session.connected_vehicle_model}
                              </p>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-400 text-sm">{session.agent_version}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-400 text-sm">{formatTimeAgo(session.last_heartbeat)}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => terminateSession(session.id)}
                            disabled={session.state === 'offline'}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredSessions.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Monitor className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Keine Sitzungen gefunden</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dongles Tab */}
        <TabsContent value="dongles" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Dongle Whitelist</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[#151c28] border-gray-700 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Dongle</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Benutzer</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Fingerprint</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Verbindungen</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Zuletzt gesehen</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDongles.map(dongle => (
                      <tr key={dongle.id} className="border-b border-gray-800/50 hover:bg-[#151c28]/50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white text-sm">{dongle.friendly_name || dongle.device_type || 'Unbekannt'}</p>
                            <p className="text-gray-500 text-xs">{dongle.device_type}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-400 text-sm">{dongle.user_email || '-'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={dongle.status} />
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-gray-500 text-xs bg-gray-800 px-2 py-1 rounded">
                            {dongle.fingerprint_hash?.substring(0, 16)}...
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-400 text-sm">{dongle.connection_count || 0}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-400 text-sm">{formatTimeAgo(dongle.last_seen_at)}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            {dongle.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                  onClick={() => approveDongle(dongle.id)}
                                  title="Genehmigen"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => blockDongle(dongle.id)}
                                  title="Blockieren"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {dongle.status === 'approved' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => blockDongle(dongle.id)}
                                title="Widerrufen"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            {dongle.status === 'blocked' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                onClick={() => approveDongle(dongle.id)}
                                title="Entsperren"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDongles.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Cpu className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Keine Dongles gefunden</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Releases Tab */}
        <TabsContent value="releases" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Agent Releases</CardTitle>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="agent-upload"
                    className="hidden"
                    accept=".zip,.exe"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      toast.loading('Datei wird hochgeladen...');
                      
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/live-obd/releases/default/upload-file/windows`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          },
                          body: formData
                        });
                        
                        toast.dismiss();
                        
                        if (response.ok) {
                          const data = await response.json();
                          toast.success(`Windows Installer hochgeladen (${(data.file_size / 1024 / 1024).toFixed(2)} MB)`);
                          fetchAllData();
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || 'Upload fehlgeschlagen');
                        }
                      } catch (error) {
                        toast.dismiss();
                        toast.error('Upload-Fehler: ' + error.message);
                      }
                      
                      e.target.value = '';
                    }}
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-white border-gray-600 hover:bg-gray-700"
                    onClick={() => document.getElementById('agent-upload')?.click()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Windows Agent hochladen
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {releases.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Download className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Keine Releases vorhanden</p>
                    <p className="text-sm mt-1">Laden Sie den Windows Agent hoch, um ihn für Benutzer verfügbar zu machen</p>
                  </div>
                ) : (
                  releases.map(release => (
                    <div key={release.id} className="p-4 bg-[#151c28] rounded-lg border border-gray-800">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold">Version {release.version}</h3>
                            {release.is_latest && (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                Aktuell
                              </Badge>
                            )}
                            <Badge className={`
                              ${release.update_policy === 'mandatory' ? 'bg-red-500/20 text-red-400' : ''}
                              ${release.update_policy === 'recommended' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                              ${release.update_policy === 'optional' ? 'bg-gray-500/20 text-gray-400' : ''}
                            `}>
                              {release.update_policy}
                            </Badge>
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{release.release_notes}</p>
                          <p className="text-gray-500 text-xs mt-2">
                            Veröffentlicht: {formatDate(release.release_date)} • 
                            Downloads: {release.download_count || 0}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {release.download_url_windows && (
                            <Button size="sm" variant="outline" className="text-white border-gray-600 hover:bg-gray-700">
                              <Download className="w-4 h-4 mr-2" />
                              Windows
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLiveOBD;
